/**
 * Permissões simples por perfil.
 *
 * 3 perfis visíveis ao tenant + owner + super_admin (plataforma).
 *   owner       → admin máximo do tenant (criou a conta)
 *   admin       → admin delegado
 *   operator    → conversas, agendamentos, clientes, veículos
 *   viewer      → consulta (read-only)
 *   super_admin → master da plataforma (todos os privilégios)
 *
 * UI deve ESCONDER itens bloqueados (não dar 403 com botão visível).
 * O backend já bloqueia via `require_admin` / `require_writer` —
 * isto aqui é a camada de UX.
 */

export const ROLE_LABELS = {
  owner: "Proprietário",
  admin: "Administrador",
  operator: "Operador",
  viewer: "Consulta",
  super_admin: "Master",
};

const ADMIN_ROLES = new Set(["owner", "admin", "super_admin"]);

/** Pode acessar a tela inteira de equipe/auditoria/financeiro/integrações. */
export function isAdmin(user) {
  return !!user && ADMIN_ROLES.has(user.role);
}

/** Pode gravar (operator+). Apenas viewer não pode. */
export function canWrite(user) {
  if (!user) return false;
  return user.role !== "viewer";
}

/**
 * Áreas do sistema (chave usada na sidebar + topbar para hide/show).
 * Lista deliberadamente curta — UX simples, sem matrix complexa.
 */
const AREA_RULES = {
  // operacional — todos os perfis exceto viewer (que tem read-only no front)
  conversations: ["owner", "admin", "operator", "viewer", "super_admin"],
  appointments:  ["owner", "admin", "operator", "viewer", "super_admin"],
  customers:     ["owner", "admin", "operator", "viewer", "super_admin"],
  assets:        ["owner", "admin", "operator", "viewer", "super_admin"],
  dashboard:     ["owner", "admin", "operator", "viewer", "super_admin"],
  knowledge:     ["owner", "admin", "operator", "viewer", "super_admin"],
  // restritos a admin / owner
  financial:     ["owner", "admin", "super_admin"],
  audit:         ["owner", "admin", "super_admin"],
  team:          ["owner", "admin", "super_admin"],
  integrations:  ["owner", "admin", "super_admin"],
  decisions:     ["owner", "admin", "super_admin"],
  smart_offers:  ["owner", "admin", "super_admin"],
  messages_hub:  ["owner", "admin", "super_admin"],
  voice:         ["owner", "admin", "super_admin"],
  post_service:  ["owner", "admin", "super_admin"],
  reports:       ["owner", "admin", "super_admin"],
  identity:      ["owner", "admin", "super_admin"],
  my_account:    ["owner", "admin", "operator", "viewer", "super_admin"],
};

export function canAccess(user, area) {
  if (!user) return false;
  const allowed = AREA_RULES[area];
  if (!allowed) return true;       // unknown area → open (safe default for dev)
  return allowed.includes(user.role);
}
